import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { getReq } from "../../configs/ApiUrl.js";
import AlwaysTop from "./util/AlwaysTop.js";
import { LoadingSpinner } from "./util/LoadingSpinner.js";
import defaultImg from "../../images/defaultImg.webp";
import locationMark from "../../images/locationMark.svg";
import DataTable from "../DataTable.js";

function UserBuy() {
  const { t } = useTranslation(["userProfile", "tables", "altText", "admin"]);
  const [loading, setLoading] = useState(false);
  const [customerOrders, setCustomerOrders] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);

  useEffect(() => {
    const getCustomerOrders = async () => {
      setLoading(true);
      try {
        const ordersResp = await getReq("/order/customer");
        if (ordersResp?.success) {
          setCustomerOrders(
            ordersResp.customerOrders.flatMap((order) =>
              order.orderItems
                .filter((item) => item.product.status === "sold")
                .map((item) => ({
                  id: `${order._id}-${item.product._id}`,
                  image: item.product?.imageNames?.[0] || defaultImg,
                  title: item.product?.title,
                  location: `${order.shippingAddress?.street}, ${order.shippingAddress?.state}, ${order.shippingAddress?.city}`,
                  price: item.product?.price ?? 0,
                  deliveryPrice: item.product?.deliveryPrice ?? 0,
                  addOns: item.product?.addOns ?? [],
                  bonusDiscount: item.product?.bonusDiscount ?? 0,
                  extraTotal: item.extraTotal ?? 0,
                  grandTotal: item.grandTotal ?? "N/A",
                }))
            )
          );
        } else {
          console.error("Failed to fetch orders:", ordersResp.message);
        }
      } catch (error) {
        console.error("Error fetching orders:", error);
      } finally {
        setLoading(false);
      }
    };
    getCustomerOrders();
    AlwaysTop();
  }, []);

  if (loading) {
    return <LoadingSpinner />;
  }

  const columns = [
    {
      header: t("tables:title"),
      accessor: (row) =>
        row.title?.length > 15 ? `${row.title.substring(0, 15)}...` : row.title,
      mobile: true,
    },
    {
      header: t("tables:location"),
      accessor: (row) => row.location,
      mobile: true,
    },
    {
      header: t("admin:price"),
      accessor: (row) => `${row.price} kr.`,
      mobile: true,
    },
    {
      header: t("admin:deliveryPriceLabel"),
      accessor: (row) => `${row.deliveryPrice} kr.`,
      mobile: true,
    },
    {
      header: t("admin:addons"),
      accessor: (row) =>
        row.addOns.length > 0
          ? row.addOns.map((a) => `${a.item}: ${a.price} kr.`).join(", ")
          : t("admin:noAddons"),
      mobile: true,
    },
    {
      header: t("productPage:bonusDiscountLabel"),
      accessor: (row) => `${row.bonusDiscount} kr.`,
      mobile: true,
    },
    {
      header: t("admin:discount"),
      accessor: (row) => `${row.extraTotal} kr.`,
      mobile: true,
    },
    {
      header: t("admin:grandTotalHeader"),
      accessor: (row) => `${row.grandTotal} kr.`,
      mobile: true,
    },
  ];

  const getRowKey = (row) => row.id;

  return (
    <div className='flex flex-col items-center min-h-screen w-full'>
      <main className='flex flex-col justify-start w-full h-full flex-grow'>
        {customerOrders.length === 0 ? (
          <div className='flex items-center justify-center w-full h-96'>
            <h1 className='text-xl font-bold text-center text-gray-500'>
              {t("admin:noHistory")}
            </h1>
          </div>
        ) : (
          <DataTable
            columns={columns}
            data={customerOrders}
            getRowKey={getRowKey}
            getImage={(row) => row.image}
            renderRowActions={undefined}
            page={page}
            rowsPerPage={rowsPerPage}
            handlePageChange={(e, newPage) => setPage(newPage)}
            handleRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            emptyMessage={t("admin:noHistory")}
          />
        )}
      </main>
    </div>
  );
}

export default UserBuy;
